import {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  POLL_MS, STALE_AFTER_MS, checkLabel, checkState, checkTone, isTerminal,
} from "@/lib/autoFactCheck";

/**
 * WriteQueue — global provider that owns the "write selected ideas"
 * background job so it survives navigation away from the run page.
 *
 * Why this exists: previously the write loop lived inside
 * `IdeasList.handleGenerate`, i.e. state on a page component. If the
 * editor navigated to Library / Settings, the component unmounted but
 * the pending `await api.generateArticleAndWait()` calls kept
 * running (fetch doesn't abort). Progress was invisible until the
 * editor came back — and even then the local state was gone.
 *
 * Now the provider drives the sequential loop from the top of the
 * tree, so a floating progress bar can render on every route while
 * writing is in progress.
 *
 * Design notes:
 *   - One batch at a time. If a second batch is started while another
 *     is running we reject (would need queueing semantics we don't
 *     need yet).
 *   - Sequential (one article at a time) to keep parity with the
 *     previous behaviour + avoid hammering the LLM + WP quota.
 *   - Success/failure surface as toasts (unchanged from before) so
 *     the Notification Center picks them up.
 *   - Automatic fact check (2026-10-01): when an entry's params carry
 *     `auto_fact_check: true`, the server checks the article AFTER its
 *     write task turns ready, so the loop is already writing the next
 *     article. `watchCheck` polls the article's `auto_fact_check` state
 *     in the background (not awaited) and stores it on the item as
 *     `check`; the floater shows the results.
 */

const WriteQueueContext = createContext({
  active: false,
  total: 0,
  completed: 0,
  currentIdeaId: null,
  currentHeadline: "",
  items: [],
  isGenerating: () => false,
  startBatch: async () => {},
  dismiss: () => {},
});

/** Snip a headline for the progress bar & toast titles. */
function snip(s, n = 60) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

export function WriteQueueProvider({ children }) {
  // `items` is the source of truth for the batch. Each entry:
  //   { ideaId, headline, status: "pending"|"writing"|"done"|"error",
  //     articleId?, error? }
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(false);
  const [currentIdeaId, setCurrentIdeaId] = useState(null);
  // Batches don't restart while one is running. This ref prevents a
  // double-invocation from an accidental double-click regardless of
  // React 18 strict-mode re-runs.
  const busyRef = useRef(false);
  // Article ids whose automatic fact check is being polled, so a retry
  // or a re-render never starts a second poller for the same article.
  const watchersRef = useRef(new Set());

  const currentHeadline = (() => {
    const cur = items.find((i) => i.ideaId === currentIdeaId);
    return cur ? cur.headline : "";
  })();

  const total = items.length;
  const completed = items.filter(
    (i) =>
      i.status === "done" ||
      i.status === "error" ||
      i.status === "prompt_only",
  ).length;

  const isGenerating = useCallback(
    (ideaId) => {
      if (!active) return false;
      const entry = items.find((i) => i.ideaId === ideaId);
      return !!entry && (entry.status === "writing" || entry.status === "pending");
    },
    [active, items],
  );

  const dismiss = useCallback(() => {
    if (busyRef.current) return; // Never dismiss while a batch is in flight
    setItems([]);
    setActive(false);
    setCurrentIdeaId(null);
  }, []);

  const setCheck = useCallback((articleId, check) => {
    setItems((prev) =>
      prev.map((it) => (it.articleId === articleId ? { ...it, check } : it)),
    );
  }, []);

  /**
   * Poll one article until its automatic fact check finishes, then
   * toast the result. Runs alongside the write loop — never awaited.
   * A failed GET is retried on the next tick; after STALE_AFTER_MS the
   * check is reported as having no result.
   */
  const watchCheck = useCallback(async (articleId, headline) => {
    if (!articleId || watchersRef.current.has(articleId)) return;
    watchersRef.current.add(articleId);
    const started = Date.now();
    try {
      while (Date.now() - started < STALE_AFTER_MS + POLL_MS) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        let article;
        try {
          article = await api.getArticle(articleId);
        } catch {
          continue;
        }
        const st = checkState(article?.auto_fact_check);
        if (!st) continue;
        setCheck(articleId, st);
        if (isTerminal(st)) {
          const msg = `Fact-checked: ${snip(article?.headline || headline)} — ${checkLabel(st)}`;
          if (st.status === "error") {
            toast.error(`Fact check failed: ${snip(article?.headline || headline)} — ${st.error}`);
          } else if (checkTone(st) === "good") {
            toast.success(msg);
          } else {
            toast.warning(msg);
          }
          return;
        }
      }
      setCheck(articleId, {
        status: "error",
        error: "No result after 15 minutes — open the article and run the check again.",
      });
    } finally {
      watchersRef.current.delete(articleId);
    }
  }, [setCheck]);

  /**
   * Start a new batch. `entries` is an array of:
   *   { ideaId, headline, params }
   * where `params` is the payload for `api.generateArticleAndWait`.
   * Returns the promise that resolves when the batch is complete.
   */
  const startBatch = useCallback(async (entries) => {
    if (busyRef.current) {
      toast.error(
        "Newsdesk is already writing a batch — wait for it to finish.",
      );
      return;
    }
    if (!entries || entries.length === 0) return;

    busyRef.current = true;
    const initial = entries.map((e) => ({
      ideaId: e.ideaId,
      headline: e.headline || "",
      // Snapshot the original write params on the entry so the Retry
      // button on WriteProgressFloater can resubmit without the caller
      // needing to re-track them.
      params: e.params,
      status: "pending",
    }));
    setItems(initial);
    setActive(true);

    for (const e of entries) {
      setCurrentIdeaId(e.ideaId);
      setItems((prev) =>
        prev.map((it) =>
          it.ideaId === e.ideaId ? { ...it, status: "writing" } : it,
        ),
      );
      try {
        const article = await api.generateArticleAndWait(e.params);
        toast.success(`Wrote: ${snip(article.headline || e.headline)}`);
        const autoCheck = !!e.params?.auto_fact_check;
        setItems((prev) =>
          prev.map((it) =>
            it.ideaId === e.ideaId
              ? {
                  ...it,
                  status: "done",
                  articleId: article.id,
                  headline: article.headline || it.headline,
                  check: autoCheck
                    ? checkState(article.auto_fact_check) || { status: "running" }
                    : null,
                }
              : it,
          ),
        );
        // Not awaited: the next article starts writing right away while
        // the server fact-checks this one.
        if (autoCheck) watchCheck(article.id, article.headline || e.headline);
      } catch (err) {
        if (err?.isPromptOnly) {
          // Success toast already fired from _pollArticleTask. Mark
          // the row as prompt_only so the floater shows a neutral
          // "prompt saved" state instead of the red error style.
          setItems((prev) =>
            prev.map((it) =>
              it.ideaId === e.ideaId
                ? { ...it, status: "prompt_only", error: null }
                : it,
            ),
          );
          continue;
        }
        const rawMsg = err?.taskError
          ? err.message
          : err?.response?.data?.detail || err.message || "error";
        const label = snip(e.headline || `idea ${e.ideaId.slice(0, 8)}`, 80);
        const msg = String(rawMsg).startsWith("[")
          ? rawMsg
          : `[${label}] ${rawMsg}`;
        toast.error(`Failed to write article — ${msg}`);
        setItems((prev) =>
          prev.map((it) =>
            it.ideaId === e.ideaId
              ? { ...it, status: "error", error: String(rawMsg) }
              : it,
          ),
        );
      }
    }

    // Loop finished — leave `items` in place so the progress bar can
    // linger a moment with "5 / 5". The floater component has its
    // own auto-dismiss timer (see WriteProgressFloater).
    setCurrentIdeaId(null);
    busyRef.current = false;
  }, [watchCheck]);

  /**
   * Retry a single errored entry. Re-fires `generateArticleAndWait` with
   * the original params (which we snapshot on the entry). Used by the
   * WriteProgressFloater's per-row Retry button so editors can recover
   * from transient upstream failures (Anthropic Cloudflare hiccups etc)
   * without re-picking the writer + ideas.
   */
  const retryEntry = useCallback(async (ideaId) => {
    if (busyRef.current) {
      toast.error(
        "Newsdesk is writing another batch right now — try again once it's done.",
      );
      return;
    }
    const entry = items.find((it) => it.ideaId === ideaId);
    if (!entry || !entry.params) {
      toast.error("Can't retry — original write params were lost.");
      return;
    }
    busyRef.current = true;
    setActive(true);
    setCurrentIdeaId(ideaId);
    setItems((prev) =>
      prev.map((it) =>
        it.ideaId === ideaId
          ? { ...it, status: "writing", error: null }
          : it,
      ),
    );
    try {
      const article = await api.generateArticleAndWait(entry.params);
      toast.success(`Wrote: ${snip(article.headline || entry.headline)}`);
      const autoCheck = !!entry.params?.auto_fact_check;
      setItems((prev) =>
        prev.map((it) =>
          it.ideaId === ideaId
            ? {
                ...it,
                status: "done",
                articleId: article.id,
                headline: article.headline || it.headline,
                error: null,
                check: autoCheck
                  ? checkState(article.auto_fact_check) || { status: "running" }
                  : null,
              }
            : it,
        ),
      );
      if (autoCheck) watchCheck(article.id, article.headline || entry.headline);
    } catch (err) {
      if (err?.isPromptOnly) {
        setItems((prev) =>
          prev.map((it) =>
            it.ideaId === ideaId
              ? { ...it, status: "prompt_only", error: null }
              : it,
          ),
        );
        busyRef.current = false;
        setCurrentIdeaId(null);
        return;
      }
      const rawMsg = err?.taskError
        ? err.message
        : err?.response?.data?.detail || err.message || "error";
      const label = snip(entry.headline || `idea ${ideaId.slice(0, 8)}`, 80);
      const msg = String(rawMsg).startsWith("[")
        ? rawMsg
        : `[${label}] ${rawMsg}`;
      toast.error(`Retry failed — ${msg}`);
      setItems((prev) =>
        prev.map((it) =>
          it.ideaId === ideaId
            ? { ...it, status: "error", error: String(rawMsg) }
            : it,
        ),
      );
    }
    setCurrentIdeaId(null);
    busyRef.current = false;
  }, [items, watchCheck]);

  return (
    <WriteQueueContext.Provider
      value={{
        active,
        total,
        completed,
        currentIdeaId,
        currentHeadline,
        items,
        isGenerating,
        startBatch,
        retryEntry,
        dismiss,
      }}
    >
      {children}
    </WriteQueueContext.Provider>
  );
}

export function useWriteQueue() {
  return useContext(WriteQueueContext);
}
